// src/ui/screens/queue.ts
// THE QUEUE, SHOWN HONESTLY.
//
// Every job displays its real state — queued / uploading / failed / held / done — and, on anything
// that is not done, THE SERVER'S OWN REASON. There is no "retrying…" that hides a permanent refusal
// and no aggregate progress bar that averages away a stuck job.
//
// `held` is rendered distinctly from `failed`, because they mean different things to the person
// looking at the screen:
//   · failed — this could work next time; the retry button is real.
//   · held   — a person with access to the deployment must act first; retrying now changes nothing,
//              so the button says so and the operator action is named.

import { el, empty, field, mount, section, why } from '../dom';
import { classify } from '../../api/refusal';
import { refusalView } from '../refusal-view';
import {
  clearDone, countJobs, drain, listJobs, removeJob, retry, subscribe, type Job,
} from '../../queue/queue';

const MB = 1024 * 1024;
const fmt = (b: number): string => (b >= MB ? `${(b / MB).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`);

export async function queueScreen(root: HTMLElement): Promise<void> {
  const render = (jobs: Job[]): void => {
    const c = countJobs(jobs);
    // ── TWO REASONS TO BE HELD, AND THEY ASK OPPOSITE THINGS (26 Aug 2026) ───────────────────
    // `held` covers two permanent situations:
    //   · a DEPLOYMENT fault (no bucket, lab switched off) — someone configures it, then retry;
    //   · a MALFORMED payload (400) — the body is frozen at enqueue, so no configuration anywhere
    //     can make it valid. It has to be removed and made again.
    // The banner below said only the first. The founder's screen showed seven jobs minted with the
    // invented mode NET_PRACTICE being blamed on a deployment that nobody could fix, next to a
    // button offering to retry once it was. The state machine already draws this distinction —
    // `neverSucceedsOnRetry()` was written to keep the two apart — and the words threw it away.
    const heldMalformed = jobs.filter((j) => j.state === 'held' && j.last_error_kind === 'invalid_request').length;
    const heldDeployment = c.held - heldMalformed;
    mount(root,
      section('Offline queue',
        why('Jobs live in IndexedDB. They survive a reload, a force-quit and an OS restart, and they '
          + 'replay with the idempotency keys minted when they were created — so a replay is '
          + 'recognised by the server and writes nothing new.'),
        el('div', { class: 'counts' },
          field('Queued', c.queued),
          field('Uploading', c.uploading),
          field('Failed', c.failed),
          field('Held', c.held),
          field('Done', c.done),
        ),
        el('div', { class: 'row' },
          el('button', { class: 'btn btn-primary', onclick: () => void drain() }, 'Send everything runnable'),
          el('button', { class: 'btn', onclick: () => void clearDone() }, 'Clear completed'),
        ),
        c.held > 0 && el('p', { class: 'warn' },
          `${c.held} job(s) are HELD. Held means retrying from this device cannot change the answer. `
          + (heldMalformed > 0
            ? `${heldMalformed} of them were refused as MALFORMED — the request was built wrong, and its body is frozen so that a retry is provably the same request. No change to the deployment will help those: remove them and make them again. `
            : '')
          + (heldDeployment > 0
            ? `${heldDeployment} need someone with access to the deployment to act. `
            : '')
          + 'Each one says what is missing.'),
      ),
      jobs.length
        ? el('div', { class: 'list' }, ...jobs.map(jobRow))
        : section('Jobs', empty('The queue is empty. Nothing is waiting to be sent.')),
    );
  };
  subscribe(render);
  render(await listJobs());
}

function jobRow(j: Job): HTMLElement {
  const body = el('div', { class: `row-item job-${j.state}` },
    el('div', { class: 'row' },
      el('span', { class: `tag tag-${j.state}` }, j.state),
      el('span', { class: 'mono small' }, j.kind === 'upload' ? j.file_name : j.body.mode),
    ),
    field('Kind', j.kind),
    field('Athlete', j.athlete_id),
    field('Attempts', j.attempts),
    field('Created', new Date(j.created_at).toLocaleString()),
    // THE IDEMPOTENCY KEY, on screen. It is the thing a tester needs to see is unchanged across a
    // retry, and putting it here makes "the replay used the same key" checkable by eye.
    j.kind === 'session'
      ? field('client_session_uuid', j.client_session_uuid)
      : field('client_upload_uuid', j.client_upload_uuid),
    j.kind === 'session' && field('client_device_id', j.client_device_id),
    j.kind === 'upload' && field('Size', fmt(j.total_bytes)),
    j.kind === 'upload' && field('Chunks acknowledged',
      j.parts_total ? `${j.parts_acknowledged} of ${j.parts_total}` : 'not started'),
    j.kind === 'upload' && field('Safe to delete locally',
      j.safe_to_delete_local_bytes === null ? 'nothing yet' : fmt(j.safe_to_delete_local_bytes)),
  );

  if (j.state === 'done') {
    body.append(el('p', { class: 'ok' },
      j.replayed === true
        ? 'Done — and the server recognised it as a replay, so nothing was written a second time.'
        : 'Done — accepted by the server.'));
    if (j.kind === 'session' && j.server_session_id) body.append(field('Session id', j.server_session_id));
    if (j.kind === 'upload' && j.media_id) body.append(field('Media id', j.media_id));
  }

  if (j.last_error) {
    // Rendered through the same refusal view as a live failure — one renderer, no drift.
    const d = (j.last_error_detail ?? {}) as Record<string, unknown>;
    const r = classify(
      typeof d['status'] === 'number' ? d['status'] : 0,
      {
        message: j.last_error,
        reason: d['reason'] ?? null,
        switch_name: d['switch_name'] ?? null,
        missing: d['missing'] ?? [],
        note: d['note'] ?? null,
        ...(j.last_error_kind === 'lab_stopped' ? { error: 'practice_lab_unavailable' } : {}),
        ...(j.last_error_kind === 'not_mounted' ? { unmounted: true } : {}),
      },
      typeof d['request'] === 'string' ? d['request'] : `${j.kind} job`,
    );
    body.append(refusalView(r, { onRetry: () => void retry(j.id) }));
  }

  const actions = el('div', { class: 'row' });
  if (j.state === 'held' && j.last_error_kind === 'invalid_request') {
    // No retry button at all. A malformed request is frozen BY DESIGN — retrying re-sends the
    // identical bytes the server already refused, so no label here could be honest about it.
    // Offering one is what carried the founder's NET_PRACTICE job to 88 attempts. Say what works.
    actions.append(el('p', { class: 'small' },
      'This request was built wrong and cannot be repaired from here — its body is frozen so that a '
      + 'retry is provably the same request, which is what makes replays safe. Remove it and create '
      + 'a new one.'));
  } else if (j.state === 'held') {
    // The retry IS offered here, unlike inside the refusal view — because on a held job the user
    // may know something the app does not (the Founder just configured R2). It is labelled for what
    // it is, so it does not read as "the app thinks this might work".
    actions.append(el('button', { class: 'btn', onclick: () => void retry(j.id) },
      'Try again (only useful once the deployment is fixed)'));
  } else if (j.state === 'failed') {
    actions.append(el('button', { class: 'btn', onclick: () => void retry(j.id) }, 'Retry'));
  }
  actions.append(el('button', { class: 'btn btn-quiet', onclick: () => void removeJob(j.id) }, 'Remove'));
  body.append(actions);
  return body;
}
